import React, { useState, useEffect } from 'react';
import Button from '../Button/Button';
import './EditUserModal.css';

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (userId: string, userData: { nom: string; prenom: string; email: string; fonction: string }) => void;
  userId: string;
  userName: string;
  userEmail: string;
  userPrenom?: string;
  userFonction?: string;
  userRole?: string; // Ajout du rôle pour déterminer la route API
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  onSave,
  userId,
  userName,
  userEmail,
  userPrenom = '',
  userFonction = '',
  userRole = ''
}) => {
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [email, setEmail] = useState('');
  const [fonction, setFonction] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ nom?: string; prenom?: string; email?: string; fonction?: string }>({});

  // Reset form when user changes
  useEffect(() => {
    if (isOpen) {
      setNom(userName || '');
      setPrenom(userPrenom || '');
      setEmail(userEmail || '');
      setFonction(userFonction || '');
      setErrors({});
    }
  }, [isOpen, userName, userPrenom, userEmail, userFonction]);

  // Handle Escape key press
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: { nom?: string; prenom?: string; email?: string; fonction?: string } = {};

    if (!nom.trim()) {
      newErrors.nom = 'Le nom est requis';
    }

    if (!prenom.trim()) {
      newErrors.prenom = 'Le prénom est requis';
    }

    if (!email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getApiRoute = (role: string, id: string): string => {
    const roleLower = role.toLowerCase();

    switch (true) {
      case roleLower.includes('acheteur signataire'):
        return `${process.env.REACT_APP_API_URL}/validateurs/${id}`;

      case roleLower.includes('acheteur'):
        return `${process.env.REACT_APP_API_URL}/acheteurs/${id}`;

      case roleLower.includes('prestataire signataire'):
        return `${process.env.REACT_APP_API_URL}/signataires/${id}`;

      case roleLower.includes('prestataire'):
        return `${process.env.REACT_APP_API_URL}/prestataires/${id}`;

      default:
        return `${process.env.REACT_APP_API_URL}/users/${id}`;
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Obtenir la route API appropriée
      const apiRoute = getApiRoute(userRole, userId);
      console.log(`Mise à jour utilisateur via route: ${apiRoute}`);

      // Effectuer la requête API
      const token = localStorage.getItem('token');
      const response = await fetch(apiRoute, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          nom: nom,
          prenom: prenom,
          email: email,
          fonction: fonction
        })
      });

      if (!response.ok) {
        throw new Error(`Erreur API: ${response.status}`);
      }

      // Appeler la fonction de rappel pour mettre à jour l'interface
      await onSave(userId, { nom, prenom, email, fonction });
      onClose();
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay edit-user-modal" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Modifier les informations utilisateur</h2>
          <button className="modal-close-button" onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="modal-content">
          <div className="edit-user-form">
            <div className="form-row">
              <div className="form-group">
                <label>Nom</label>
                <input
                  type="text"
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  className={errors.nom ? 'error' : ''}
                  placeholder="Nom"
                />
                {errors.nom && <span className="error-message">{errors.nom}</span>}
              </div>

              <div className="form-group">
                <label>Prénom</label>
                <input
                  type="text"
                  value={prenom}
                  onChange={(e) => setPrenom(e.target.value)}
                  className={errors.prenom ? 'error' : ''}
                  placeholder="Prénom"
                />
                {errors.prenom && <span className="error-message">{errors.prenom}</span>}
              </div>
            </div>

            <div className="form-group">
              <label>Fonction</label>
              <input
                type="text"
                value={fonction}
                onChange={(e) => setFonction(e.target.value)}
                className={errors.fonction ? 'error' : ''}
                placeholder="Fonction"
              />
              {errors.fonction && <span className="error-message">{errors.fonction}</span>}
            </div>

            <div className="form-group">
              <label>Adresse email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={errors.email ? 'error' : ''}
                placeholder="email@example.com"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
          </div>

          <div className="modal-actions">
            <Button
              value="Annuler"
              variant="secondary"
              onClick={onClose}
            />
            <Button
              value={isSubmitting ? "Enregistrement..." : "Enregistrer"}
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;